import { t } from "@lingui/core/macro";
import { useEffect, useRef } from "react";

import { APP_BASE_NAME } from "../branding";
import { useStaleClientBundle } from "../hooks/useStaleClientBundle";
import { markStaleClientReloadRequested } from "../versionSkew";
import { planStaleClientToast } from "./StaleClientToastCoordinator.logic";
import { stackedThreadToast, toastManager } from "./ui/toast";

type StaleClientToastId = ReturnType<typeof toastManager.add>;

export function StaleClientToastCoordinator() {
  const staleClientBundle = useStaleClientBundle();
  const staleKey = staleClientBundle?.staleKey ?? null;
  const toastIdRef = useRef<StaleClientToastId | null>(null);
  const shownKeyRef = useRef<string | null>(null);
  const dismissedAtRef = useRef<number | null>(null);
  const reminderTimerRef = useRef<number | null>(null);

  useEffect(() => {
    function clearReminder() {
      if (reminderTimerRef.current !== null) {
        window.clearTimeout(reminderTimerRef.current);
        reminderTimerRef.current = null;
      }
    }
    function closeToast() {
      const toastId = toastIdRef.current;
      toastIdRef.current = null;
      if (toastId !== null) {
        toastManager.close(toastId);
      }
    }
    function showToast(key: string) {
      closeToast();
      shownKeyRef.current = key;
      dismissedAtRef.current = null;
      const toastId = toastManager.add({
        ...stackedThreadToast({
          type: "warning",
          title: t`This page is out of date`,
          description: t`Refresh the page to keep ${APP_BASE_NAME} working correctly.`,
          timeout: 0,
          actionProps: {
            children: t`Refresh`,
            onClick: () => {
              markStaleClientReloadRequested(key);
              window.location.reload();
            },
          },
          data: { hideCopyButton: true },
        }),
        onClose: () => {
          if (toastIdRef.current !== toastId) {
            return;
          }
          toastIdRef.current = null;
          dismissedAtRef.current = Date.now();
          run();
        },
      });
      toastIdRef.current = toastId;
    }
    function run() {
      clearReminder();
      if (staleKey === null) {
        closeToast();
        shownKeyRef.current = null;
        dismissedAtRef.current = null;
        return;
      }
      const plan = planStaleClientToast(
        {
          staleKey,
          shownKey: shownKeyRef.current,
          dismissedAtMs: dismissedAtRef.current,
        },
        Date.now(),
      );
      if (plan.action === "show") {
        showToast(staleKey);
      } else if (plan.action === "wait") {
        reminderTimerRef.current = window.setTimeout(run, plan.delayMs);
      }
    }

    run();
    return () => {
      clearReminder();
      closeToast();
    };
  }, [staleKey]);

  return null;
}
